import { sdk } from '../../config'
import {
  ArchiveAutomatedVehicleReminderRequest,
  AutomatedVehicleReminderEntry,
  CreateAutomatedVehicleReminderRequest,
  CreateVehicleFluidsRequest,
  CreateVehicleOilFilterRequest,
  CreateVehicleReminderMessageRequest,
  GetAutomatedVehicleReminderEntriesByIdRequest,
  GetUnarchivedAutomatedVehicleReminderByRdateRequest,
  GetUpcomingUnarchivedAutomatedVehicleReminderRequest,
  ListVehicleReminderRequest,
  ListVehicleWithCustomersRequest,
  SearchVehicleFluidsRequest,
  SendVehicleReminderSmsOperationRequest,
  UpdateAutomatedVehicleReminderRequest,
  VehicleFluidsEntry,
  VehicleOilFilterEntry,
  VehicleReminder,
  VehicleWithCustomers
} from '../../sdk'
import { stripQuotes } from '../../utils'
import { api } from '.'

const searchVehicleFluids = sdk.searchVehicleFluids.bind(sdk)
const createVehicleFluids = sdk.createVehicleFluids.bind(sdk)
const searchVehicleOilFilters = sdk.searchVehicleOilFilters.bind(sdk)
const createVehicleOilFilter = sdk.createVehicleOilFilter.bind(sdk)

const listVehicleWithCustomers = sdk.listVehicleWithCustomers.bind(sdk)

const listVehicleReminder = sdk.listVehicleReminder.bind(sdk)
const createVehicleReminderMessage = sdk.createVehicleReminderMessage.bind(sdk)
const sendVehicleReminderSms = sdk.sendVehicleReminderSms.bind(sdk)

const createAutomatedVehicleReminder =
  sdk.createAutomatedVehicleReminder.bind(sdk)
const updateAutomatedVehicleReminder =
  sdk.updateAutomatedVehicleReminder.bind(sdk)
const archiveAutomatedVehicleReminder =
  sdk.archiveAutomatedVehicleReminder.bind(sdk)
const getAutomatedVehicleReminderEntriesById =
  sdk.getAutomatedVehicleReminderEntriesById.bind(sdk)
const getUnarchivedAutomatedVehicleReminderByRdate =
  sdk.getUnarchivedAutomatedVehicleReminderByRdate.bind(sdk)
const getUpcomingUnarchivedAutomatedVehicleReminder =
  sdk.getUpcomingUnarchivedAutomatedVehicleReminder.bind(sdk)

const vehicleApi = api.injectEndpoints({
  endpoints: (builder) => ({
    searchVehicleFluids: builder.query<
      VehicleFluidsEntry[],
      SearchVehicleFluidsRequest
    >({
      query: (req) => () => searchVehicleFluids(req),
      providesTags: ['VehicleFluids' as const]
    }),
    createVehicleFluids: builder.mutation<string, CreateVehicleFluidsRequest>({
      query: (req) => () => createVehicleFluids(req),
      transformResponse: (response: string) => stripQuotes(response),
      invalidatesTags: ['VehicleFluids' as const]
    }),
    searchVehicleOilFilters: builder.query<
      VehicleOilFilterEntry[],
      SearchVehicleFluidsRequest
    >({
      query: (req) => () => searchVehicleOilFilters(req),
      providesTags: ['VehicleOilFilter' as const]
    }),
    createVehicleOilFilter: builder.mutation<
      string,
      CreateVehicleOilFilterRequest
    >({
      query: (req) => () => createVehicleOilFilter(req),
      transformResponse: (response: string) => stripQuotes(response),
      invalidatesTags: ['VehicleOilFilter' as const]
    }),
    listVehicleWithCustomers: builder.query<
      VehicleWithCustomers[],
      ListVehicleWithCustomersRequest
    >({
      query: (req) => () => listVehicleWithCustomers(req),
      providesTags: (result) =>
        result
          ? [
              ...result.map((entry) => ({
                type: 'Vehicle' as const,
                id: entry.vehicleEntry!.id!
              })),
              'Vehicle'
            ]
          : ['Vehicle' as const]
    }),
    listVehicleReminder: builder.query<
      VehicleReminder[],
      ListVehicleReminderRequest
    >({
      query: (req) => () => listVehicleReminder(req),
      providesTags: (result) =>
        result
          ? [
              ...result.map((entry) => ({
                type: 'Vehicle' as const,
                id: entry.vehicleEntry!.id!
              })),
              'Vehicle'
            ]
          : ['Vehicle' as const]
    }),
    createVehicleReminderMessage: builder.mutation<
      string,
      CreateVehicleReminderMessageRequest
    >({
      query: (req) => () => createVehicleReminderMessage(req),
      transformResponse: (response: string) => stripQuotes(response),
      invalidatesTags: (result, error, req) => [
        {
          type: 'Vehicle',
          id: req.vehicleReminderMessageInfo?.vehicle
        }
      ]
    }),
    sendVehicleReminderSms: builder.mutation<
      string,
      SendVehicleReminderSmsOperationRequest
    >({
      query: (req) => () => sendVehicleReminderSms(req),
      transformResponse: (response: string) => stripQuotes(response),
      invalidatesTags: (result, error, req) => [
        {
          type: 'Vehicle',
          id: req.sendVehicleReminderSmsRequest?.info?.vehicle
        }
      ]
    }),
    createAutomatedVehicleReminder: builder.mutation<
      string,
      CreateAutomatedVehicleReminderRequest
    >({
      query: (req) => () => createAutomatedVehicleReminder(req),
      transformResponse: (response: string) => stripQuotes(response),
      invalidatesTags: ['AutomatedVehicleReminder' as const]
    }),
    updateAutomatedVehicleReminder: builder.mutation<
      void,
      UpdateAutomatedVehicleReminderRequest
    >({
      query: (req) => () => updateAutomatedVehicleReminder(req),
      invalidatesTags: (result, error, req) => [
        {
          type: 'AutomatedVehicleReminder',
          id: req.automatedVehicleReminderEntry?.id
        }
      ]
    }),
    archiveAutomatedVehicleReminder: builder.mutation<
      void,
      ArchiveAutomatedVehicleReminderRequest
    >({
      query: (req) => () => archiveAutomatedVehicleReminder(req),
      invalidatesTags: (result, error, req) => [
        {
          type: 'AutomatedVehicleReminder',
          id: req.id
        }
      ]
    }),
    getAutomatedVehicleReminderEntriesById: builder.query<
      AutomatedVehicleReminderEntry[],
      GetAutomatedVehicleReminderEntriesByIdRequest
    >({
      query: (req) => () => getAutomatedVehicleReminderEntriesById(req),
      providesTags: (result) =>
        result
          ? [
              ...result.map((entry) => ({
                type: 'AutomatedVehicleReminder' as const,
                id: entry.id
              })),
              'AutomatedVehicleReminder'
            ]
          : ['AutomatedVehicleReminder' as const]
    }),
    getUnarchivedAutomatedVehicleReminderByRdate: builder.query<
      AutomatedVehicleReminderEntry[],
      GetUnarchivedAutomatedVehicleReminderByRdateRequest
    >({
      query: (req) => () => getUnarchivedAutomatedVehicleReminderByRdate(req),
      providesTags: (result) =>
        result
          ? [
              ...result.map((entry) => ({
                type: 'AutomatedVehicleReminder' as const,
                id: entry.id
              })),
              'AutomatedVehicleReminder'
            ]
          : ['AutomatedVehicleReminder' as const]
    }),
    getUpcomingUnarchivedAutomatedVehicleReminder: builder.query<
      AutomatedVehicleReminderEntry[],
      GetUpcomingUnarchivedAutomatedVehicleReminderRequest
    >({
      query: (req) => () => getUpcomingUnarchivedAutomatedVehicleReminder(req),
      providesTags: (result) =>
        result
          ? [
              ...result.map((entry) => ({
                type: 'AutomatedVehicleReminder' as const,
                id: entry.id
              })),
              'AutomatedVehicleReminder'
            ]
          : ['AutomatedVehicleReminder' as const]
    })
  }),

  overrideExisting: true
})

export const {
  useSearchVehicleFluidsQuery,
  useCreateVehicleFluidsMutation,
  useSearchVehicleOilFiltersQuery,
  useCreateVehicleOilFilterMutation,
  useListVehicleWithCustomersQuery,
  useLazyListVehicleWithCustomersQuery,
  useListVehicleReminderQuery,
  useLazyListVehicleReminderQuery,
  useCreateVehicleReminderMessageMutation,
  useSendVehicleReminderSmsMutation,
  useCreateAutomatedVehicleReminderMutation,
  useUpdateAutomatedVehicleReminderMutation,
  useArchiveAutomatedVehicleReminderMutation,
  useGetAutomatedVehicleReminderEntriesByIdQuery,
  useGetUnarchivedAutomatedVehicleReminderByRdateQuery,
  useGetUpcomingUnarchivedAutomatedVehicleReminderQuery
} = vehicleApi
